import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// JWT auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vs_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for subscription checks + token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403) {
      const detail = error.response.data?.detail;
      if (detail?.code === 'SUBSCRIPTION_INACTIVE') {
        window.location.href = '/pricing';
        return Promise.reject(error);
      }
    }
    // Auto-refresh expired tokens (only if we had a token to begin with)
    if (error.response?.status === 401 && !error.config._retry) {
      const existingToken = localStorage.getItem('vs_token');
      if (existingToken && !error.config.url?.includes('/auth/refresh') && !error.config.url?.includes('/auth/login')) {
        error.config._retry = true;
        try {
          const { data } = await api.post('/auth/refresh');
          if (data.token) {
            localStorage.setItem('vs_token', data.token);
            error.config.headers.Authorization = `Bearer ${data.token}`;
            return api(error.config);
          }
        } catch {}
        localStorage.removeItem('vs_token');
      }
      // NEVER redirect from the interceptor — let React Router handle it
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register-demo', data);  // Use demo signup (free trial)
export const registerPaid = (data) => api.post('/auth/register', data);  // Paid signup (if needed)
export const getMe = () => api.get('/auth/me');

// Plans
export const getPlans = () => api.get('/plans');

// Stripe
export const createCheckoutSession = (data) => api.post('/stripe/create-checkout-session', data);
export const getCheckoutStatus = (sessionId) => api.get(`/stripe/checkout-status/${sessionId}`);
export const getSubscriptionStatus = () => api.get('/stripe/subscription-status');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Vendors
export const getVendors = (params) => api.get('/vendors', { params });
export const getVendor = (id) => api.get(`/vendors/${id}`);
export const createVendor = (data) => api.post('/vendors', data);
export const updateVendor = (id, data) => api.put(`/vendors/${id}`, data);
export const deleteVendor = (id) => api.delete(`/vendors/${id}`);

// Properties
export const getProperties = (params) => api.get('/properties', { params });
export const getProperty = (id) => api.get(`/properties/${id}`);
export const createProperty = (data) => api.post('/properties', data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data);
export const deleteProperty = (id) => api.delete(`/properties/${id}`);

// Documents
export const getDocuments = (params) => api.get('/documents', { params });
export const getDocument = (id) => api.get(`/documents/${id}`);
export const createDocument = (data) => api.post('/documents', data);
export const updateDocument = (id, data) => api.put(`/documents/${id}`, data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const triggerExtraction = (id) => api.post(`/documents/${id}/extract`);
export const uploadDocument = (formData) => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getDocumentDownloadUrl = (id) => api.get(`/documents/${id}/download-url`);
export const reprocessDocument = (id) => api.post(`/documents/${id}/reprocess`);
export const approveExtraction = (id, data) => api.post(`/documents/${id}/approve-extraction`, data);

// Compliance Rules
export const getComplianceRules = (params) => api.get('/compliance-rules', { params });
export const createComplianceRule = (data) => api.post('/compliance-rules', data);
export const updateComplianceRule = (id, data) => api.put(`/compliance-rules/${id}`, data);
export const deleteComplianceRule = (id) => api.delete(`/compliance-rules/${id}`);

// Scoring
export const scoreVendor = (vendorId, propertyId) =>
  api.post(`/compliance/score/${vendorId}`, null, { params: { property_id: propertyId } });

// Communications
export const getEmailLogs = (params) => api.get('/email-logs', { params });
export const createEmailLog = (data) => api.post('/email-logs', data);
export const triggerChase = (vendorId) => api.post(`/chase/${vendorId}`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Portal
export const generateMagicLink = (vendorId) => api.post(`/portal/generate-link/${vendorId}`);
export const verifyPortalToken = (token) => api.get(`/portal/verify/${token}`);
export const portalUploadDocument = (token, data) => api.post(`/portal/upload/${token}`, data);

// Reports
export const getPortfolioReport = (params) => api.get('/reports/portfolio', { params });
export const getVendorReport = (vendorId) => api.get(`/reports/vendor/${vendorId}`);

// Bulk Import
export const createBulkImport = (data) => api.post('/bulk-import', data);
export const getBulkImports = (params) => api.get('/bulk-import', { params });
export const processBulkImport = (jobId, data) => api.post(`/bulk-import/${jobId}/process`, data);

// Users
export const getUsers = (params) => api.get('/users', { params });
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Settings
export const getSettings = (params) => api.get('/settings', { params });
export const updateSettings = (data) => api.put('/settings', data);

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });

// Property Requirements
export const getPropertyRequirements = (params) => api.get('/property-requirements', { params });
export const createPropertyRequirement = (data) => api.post('/property-requirements', data);
export const deletePropertyRequirement = (id) => api.delete(`/property-requirements/${id}`);


// Coverage Comparison
export const getVendorPropertyCoverage = (vendorId, propertyId) => api.get(`/coverage/vendor/${vendorId}/property/${propertyId}`);
export const getVendorAllCoverage = (vendorId) => api.get(`/coverage/vendor/${vendorId}`);
export const getPropertyAllCoverage = (propertyId) => api.get(`/coverage/property/${propertyId}`);
export const getCoverageFields = () => api.get('/coverage/fields');
